// Analytics types
export interface StudentGrade {
    date: string;
    score: number;
    subject: string;
    testName?: string;
}

export interface StudentSubject {
    subject: string;
    averageScore: number;
    testsCount: number;
}

export interface TestMistake {
    question: string;
    topic: string;
    error: string;
}

export interface StudentTest {
    id: number;
    name: string;
    date: string;
    score: number;
    passed: boolean;
    totalQuestions?: number;
    correctAnswers?: number;
    timeSpent?: string;
    feedback?: string;
    topics?: string[];
    mistakes?: TestMistake[];
}

export interface StudentStats {
    averageScore: number;
    totalTests: number;
    lastActivity?: string;
}

export interface StudentAnalytics {
    stats: StudentStats;
    grades: StudentGrade[];
    subjects: StudentSubject[];
    tests: StudentTest[];
}

export interface IStudentPageState {
    student: any,
    grades: StudentGrade[],
    assignments: StudentTest[],
    attendance: [],
    analytics: StudentAnalytics | null,
    loading: boolean,
    error: string,
    success: boolean,
    grades_loading: boolean,
    assignments_loading: boolean,
    attendance_loading: boolean,
    analytics_loading: boolean,
}

//#region -----------------CRUD Models----------------------------

export interface GetStudentDetailsModel {
    id: number
}

export interface GetStudentGradesModel {
    studentId: number
}

export interface GetStudentAssignmentsModel {
    studentId: number
}

export interface GetStudentAttendanceModel {
    studentId: number
}

export interface UpdateStudentDetailsModel {
    id: number,
    firstName: string,
    lastName: string,
    email?: string,
    phone?: string,
    dateOfBirth?: string,
    classId: number
}

//#endregion

//#region // -----------------CRUD Types----------------------------

export enum GetStudentDetailsActionsTypes {
    GET_STUDENT_DETAILS = 'GET_STUDENT_DETAILS',
    GET_STUDENT_DETAILS_SUCCESS = 'GET_STUDENT_DETAILS_SUCCESS',
    GET_STUDENT_DETAILS_ERROR = 'GET_STUDENT_DETAILS_ERROR'
}

export enum GetStudentGradesActionsTypes {
    GET_STUDENT_GRADES = 'GET_STUDENT_GRADES',
    GET_STUDENT_GRADES_SUCCESS = 'GET_STUDENT_GRADES_SUCCESS',
    GET_STUDENT_GRADES_ERROR = 'GET_STUDENT_GRADES_ERROR'
}

export enum GetStudentAssignmentsActionsTypes {
    GET_STUDENT_ASSIGNMENTS = 'GET_STUDENT_ASSIGNMENTS',
    GET_STUDENT_ASSIGNMENTS_SUCCESS = 'GET_STUDENT_ASSIGNMENTS_SUCCESS',
    GET_STUDENT_ASSIGNMENTS_ERROR = 'GET_STUDENT_ASSIGNMENTS_ERROR'
}

export enum GetStudentAttendanceActionsTypes {
    GET_STUDENT_ATTENDANCE = 'GET_STUDENT_ATTENDANCE',
    GET_STUDENT_ATTENDANCE_SUCCESS = 'GET_STUDENT_ATTENDANCE_SUCCESS',
    GET_STUDENT_ATTENDANCE_ERROR = 'GET_STUDENT_ATTENDANCE_ERROR'
}

export enum UpdateStudentDetailsActionsTypes {
    PUT_UPDATE_STUDENT_DETAILS = 'PUT_UPDATE_STUDENT_DETAILS',
    PUT_UPDATE_STUDENT_DETAILS_SUCCESS = 'PUT_UPDATE_STUDENT_DETAILS_SUCCESS',
    PUT_UPDATE_STUDENT_DETAILS_ERROR = 'PUT_UPDATE_STUDENT_DETAILS_ERROR'
}

export enum GetStudentAnalyticsActionsTypes {
    GET_STUDENT_ANALYTICS = 'GET_STUDENT_ANALYTICS',
    GET_STUDENT_ANALYTICS_SUCCESS = 'GET_STUDENT_ANALYTICS_SUCCESS',
    GET_STUDENT_ANALYTICS_ERROR = 'GET_STUDENT_ANALYTICS_ERROR'
}

//#endregion

//#region // -----------------CRUD Requests----------------------------

//#region // -----------------Get Student Details Request----------------------------

export interface GetStudentDetailsRequest {
    type: GetStudentDetailsActionsTypes.GET_STUDENT_DETAILS
}

export interface GetStudentDetailsRequestSuccess {
    type: GetStudentDetailsActionsTypes.GET_STUDENT_DETAILS_SUCCESS,
    data: any,
    loading: boolean
}

export interface GetStudentDetailsRequestError {
    type: GetStudentDetailsActionsTypes.GET_STUDENT_DETAILS_ERROR,
    error: string,
    loading: boolean
}

//#endregion

//#region // -----------------Get Student Grades Request----------------------------

export interface GetStudentGradesRequest {
    type: GetStudentGradesActionsTypes.GET_STUDENT_GRADES
}

export interface GetStudentGradesRequestSuccess {
    type: GetStudentGradesActionsTypes.GET_STUDENT_GRADES_SUCCESS,
    data: any[],
    loading: boolean
}

export interface GetStudentGradesRequestError {
    type: GetStudentGradesActionsTypes.GET_STUDENT_GRADES_ERROR,
    error: string,
    loading: boolean
}

//#endregion

//#region // -----------------Get Student Assignments Request----------------------------

export interface GetStudentAssignmentsRequest {
    type: GetStudentAssignmentsActionsTypes.GET_STUDENT_ASSIGNMENTS
}

export interface GetStudentAssignmentsRequestSuccess {
    type: GetStudentAssignmentsActionsTypes.GET_STUDENT_ASSIGNMENTS_SUCCESS,
    data: any[],
    loading: boolean
}

export interface GetStudentAssignmentsRequestError {
    type: GetStudentAssignmentsActionsTypes.GET_STUDENT_ASSIGNMENTS_ERROR,
    error: string,
    loading: boolean
}

//#endregion

//#region // -----------------Get Student Attendance Request----------------------------

export interface GetStudentAttendanceRequest {
    type: GetStudentAttendanceActionsTypes.GET_STUDENT_ATTENDANCE
}

export interface GetStudentAttendanceRequestSuccess {
    type: GetStudentAttendanceActionsTypes.GET_STUDENT_ATTENDANCE_SUCCESS,
    data: any[],
    loading: boolean
}

export interface GetStudentAttendanceRequestError {
    type: GetStudentAttendanceActionsTypes.GET_STUDENT_ATTENDANCE_ERROR,
    error: string,
    loading: boolean
}

//#endregion

//#region // -----------------Update Student Details Request----------------------------

export interface UpdateStudentDetailsRequest {
    type: UpdateStudentDetailsActionsTypes.PUT_UPDATE_STUDENT_DETAILS
}

export interface UpdateStudentDetailsRequestSuccess {
    type: UpdateStudentDetailsActionsTypes.PUT_UPDATE_STUDENT_DETAILS_SUCCESS,
    data: any,
    loading: boolean
}

export interface UpdateStudentDetailsRequestError {
    type: UpdateStudentDetailsActionsTypes.PUT_UPDATE_STUDENT_DETAILS_ERROR,
    error: string,
    loading: boolean
}

//#endregion

//#region // -----------------Get Student Analytics Request----------------------------

export interface GetStudentAnalyticsRequest {
    type: GetStudentAnalyticsActionsTypes.GET_STUDENT_ANALYTICS
}

export interface GetStudentAnalyticsRequestSuccess {
    type: GetStudentAnalyticsActionsTypes.GET_STUDENT_ANALYTICS_SUCCESS,
    data: StudentAnalytics,
    loading: boolean
}

export interface GetStudentAnalyticsRequestError {
    type: GetStudentAnalyticsActionsTypes.GET_STUDENT_ANALYTICS_ERROR,
    error: string,
    loading: boolean
}

//#endregion

//#endregion

//#region // -----------------CRUD Results----------------------------

export interface IGetStudentDetailsResult {
    data: any,
    success: boolean,
    error: number
}

export interface IGetStudentGradesResult {
    data: any[],
    success: boolean,
    error: number
}

export interface IGetStudentAssignmentsResult {
    data: any[],
    success: boolean,
    error: number
}

export interface IGetStudentAttendanceResult {
    data: any[],
    success: boolean,
    error: number
}

export interface IUpdateStudentDetailsResult {
    data: any,
    success: boolean,
    error: number
}

export interface IGetStudentAnalyticsResult {
    data: StudentAnalytics,
    success: boolean,
    error: number
}

//#endregion

export type StudentPageActions = 
    GetStudentDetailsRequest | GetStudentDetailsRequestSuccess | GetStudentDetailsRequestError |
    GetStudentGradesRequest | GetStudentGradesRequestSuccess | GetStudentGradesRequestError |
    GetStudentAssignmentsRequest | GetStudentAssignmentsRequestSuccess | GetStudentAssignmentsRequestError |
    GetStudentAttendanceRequest | GetStudentAttendanceRequestSuccess | GetStudentAttendanceRequestError |
    UpdateStudentDetailsRequest | UpdateStudentDetailsRequestSuccess | UpdateStudentDetailsRequestError |
    GetStudentAnalyticsRequest | GetStudentAnalyticsRequestSuccess | GetStudentAnalyticsRequestError;
